import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ImagePlus, Loader2, Send, X, Clock3, Camera, PhoneCall } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 3;
const MAX_SIZE = 8 * 1024 * 1024;

const initialState = { nombre: "", empresa: "", telefono: "", tipo_problema: "", mensaje: "" };

export default function RepairQuoteForm({ variant = "reparacion" }) {
  const isFab = variant === "fabricacion";
  const [form, setForm] = useState(initialState);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const fileInputRef = useRef(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const addFotos = (files) => {
    const incoming = Array.from(files || []);
    if (!incoming.length) return;
    const valid = [];
    for (const f of incoming) {
      if (!f.type.startsWith("image/")) {
        setTimeout(() => toast.error(`"${f.name}" no es una imagen válida.`), 0);
        continue;
      }
      if (f.size > MAX_SIZE) {
        setTimeout(() => toast.error(`"${f.name}" supera los 8 MB permitidos.`), 0);
        continue;
      }
      valid.push({ file: f, preview: URL.createObjectURL(f) });
    }
    setFotos((prev) => {
      const room = MAX_PHOTOS - prev.length;
      if (valid.length > room) {
        setTimeout(() => toast.error(`Máximo ${MAX_PHOTOS} fotos.`), 0);
      }
      const accepted = valid.slice(0, Math.max(room, 0));
      valid.slice(Math.max(room, 0)).forEach((v) => URL.revokeObjectURL(v.preview));
      return [...prev, ...accepted];
    });
  };

  const removeFoto = (idx) =>
    setFotos((prev) => {
      URL.revokeObjectURL(prev[idx]?.preview);
      return prev.filter((_, i) => i !== idx);
    });

  useEffect(() => {
    return () => setFotos((prev) => {
      prev.forEach((f) => URL.revokeObjectURL(f.preview));
      return prev;
    });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.telefono || !form.tipo_problema) {
      setTimeout(() => toast.error("Por favor completa los campos requeridos."), 0);
      return;
    }
    if (!acceptedPrivacy) {
      setTimeout(() => toast.error("Debes aceptar el Aviso de Privacidad para continuar."), 0);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("origen", variant);
      fotos.forEach((f) => fd.append("fotos", f.file, f.file.name));
      await axios.post(`${API}/repair-quote`, fd);
      setForm(initialState);
      fotos.forEach((f) => URL.revokeObjectURL(f.preview));
      setFotos([]);
      setAcceptedPrivacy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setLoading(false);
      setTimeout(() => toast.success("Solicitud enviada. Te contactaremos hoy mismo."), 0);
    } catch (err) {
      setLoading(false);
      const detail = err?.response?.data?.detail || "No pudimos enviar tu solicitud. Intenta nuevamente.";
      setTimeout(() => toast.error(typeof detail === "string" ? detail : "Error al enviar"), 0);
    }
  };

  const bullets = [
    { icon: Clock3, text: "Respuesta en menos de 2 horas hábiles" },
    {
      icon: Camera,
      text: isFab
        ? "Aceptamos plano técnico, muestra física o medidas"
        : "Evaluación preliminar por foto o visita técnica para levantamiento",
    },
    { icon: PhoneCall, text: "442 431 9100 · Urgencias 24/7" },
  ];

  return (
    <section id="cotizar" data-testid="repair-quote-section" className="gf-section bg-[#061226] relative">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00B4D8]/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-[#00B4D8]" />
            <span className="gf-overline">{isFab ? "Cotización a medida" : "Cotización rápida"}</span>
          </div>
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-6">
            {isFab ? "Comparte tu plano o muestra. " : "Envíanos fotos de la falla. "}
            <span className="text-[#00B4D8]">Cotizamos hoy.</span>
          </h2>
          <p className="text-white/60 text-base leading-relaxed mb-10">
            {isFab
              ? "Comparte el plano técnico, medidas o fotos de la muestra física y el modelo de tu máquina. Fabricamos con geometría OEM en acero inoxidable."
              : `Cuéntanos qué le pasa a tu guarda y adjunta hasta ${MAX_PHOTOS} fotos del daño. Atención inmediata para evaluar el daño de tu guarda.`}
          </p>
          <ul className="space-y-5">
            {bullets.map((b) => {
              const Icon = b.icon;
              return (
                <li key={b.text} className="flex items-center gap-4">
                  <span className="w-10 h-10 border border-white/15 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-[#00B4D8]" />
                  </span>
                  <span className="text-white/75 text-sm">{b.text}</span>
                </li>
              );
            })}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={onSubmit}
            data-testid="repair-quote-form"
            noValidate
            className="bg-white text-slate-900 p-8 lg:p-10 relative"
            style={{ borderRadius: "2px" }}
          >
            <div className="absolute -top-px left-8 right-8 h-px bg-[#00B4D8]" />
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#0096B4] mb-2">
              Formulario · {isFab ? "GF-FAB-01" : "GF-RP-01"}
            </div>
            <h3 className="font-display font-black text-2xl text-[#0A1B35] mb-8 tracking-tight">
              {isFab ? "Cotiza tu fabricación" : "Cotiza tu reparación"}
            </h3>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="gf-label" htmlFor="rp-nombre">
                  Nombre <span className="text-[#00B4D8]">*</span>
                </label>
                <input
                  id="rp-nombre"
                  name="nombre"
                  value={form.nombre}
                  onChange={onChange}
                  data-testid="repair-input-nombre"
                  placeholder="Tu nombre completo"
                  className="gf-input"
                  required
                />
              </div>
              <div>
                <label className="gf-label" htmlFor="rp-empresa">Empresa</label>
                <input
                  id="rp-empresa"
                  name="empresa"
                  value={form.empresa}
                  onChange={onChange}
                  data-testid="repair-input-empresa"
                  placeholder="Razón social"
                  className="gf-input"
                />
              </div>
              <div>
                <label className="gf-label" htmlFor="rp-telefono">
                  Teléfono <span className="text-[#00B4D8]">*</span>
                </label>
                <input
                  id="rp-telefono"
                  name="telefono"
                  value={form.telefono}
                  onChange={onChange}
                  data-testid="repair-input-telefono"
                  placeholder="+52 ..."
                  className="gf-input"
                  required
                />
              </div>
              <div>
                <label className="gf-label" htmlFor="rp-problema">
                  {isFab ? "Sistema a fabricar / máquina" : "Falla o modelo de máquina"} <span className="text-[#00B4D8]">*</span>
                </label>
                <input
                  id="rp-problema"
                  name="tipo_problema"
                  value={form.tipo_problema}
                  onChange={onChange}
                  data-testid="repair-input-problema"
                  placeholder={isFab ? "Ej. Guarda telescópica para Haas VF-2 — eje X" : "Ej. Guarda Haas VF-2 — no desplaza eje X"}
                  className="gf-input"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="gf-label" htmlFor="rp-mensaje">Detalle adicional (opcional)</label>
                <textarea
                  id="rp-mensaje"
                  name="mensaje"
                  value={form.mensaje}
                  onChange={onChange}
                  data-testid="repair-input-mensaje"
                  placeholder={isFab ? "Recorrido aproximado, marca/modelo de la máquina, material requerido..." : "Cuándo empezó la falla, si la máquina está parada, medidas aproximadas..."}
                  rows={4}
                  className="gf-input resize-none"
                />
              </div>

              <div className="sm:col-span-2">
                <span className="gf-label">{isFab ? "Plano o fotos de referencia" : "Fotos del daño"} (hasta {MAX_PHOTOS})</span>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="repair-upload-trigger"
                  className="w-full border-2 border-dashed border-slate-300 hover:border-[#00B4D8] bg-slate-50 hover:bg-[#00B4D8]/5 transition-colors p-6 flex flex-col items-center gap-2 text-slate-500 hover:text-[#0096B4]"
                  style={{ borderRadius: "2px" }}
                >
                  <ImagePlus size={22} />
                  <span className="text-sm font-medium">
                    {isFab
                      ? "Toca para adjuntar plano o fotos de la muestra"
                      : "Toca para adjuntar fotos de la guarda dañada"}
                  </span>
                  <span className="text-xs text-slate-400">JPG, PNG o WEBP · Máx. 8 MB c/u</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  data-testid="repair-input-fotos"
                  className="hidden"
                  onChange={(e) => {
                    addFotos(e.target.files);
                    e.target.value = "";
                  }}
                />
                {fotos.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-3" data-testid="repair-photo-previews">
                    {fotos.map((f, i) => (
                      <div key={`${f.file.name}-${i}`} className="relative group" data-testid={`repair-photo-preview-${i}`}>
                        <img
                          src={f.preview}
                          alt={f.file.name}
                          className="w-full h-24 object-cover border border-slate-200"
                          style={{ borderRadius: "2px" }}
                        />
                        <button
                          type="button"
                          onClick={() => removeFoto(i)}
                          data-testid={`repair-photo-remove-${i}`}
                          aria-label={`Quitar foto ${i + 1}`}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-[#0A1B35] text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                          style={{ borderRadius: "2px" }}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <label
                htmlFor="rp-privacy"
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="repair-privacy-wrapper"
              >
                <input
                  id="rp-privacy"
                  type="checkbox"
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  data-testid="repair-privacy-checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#00B4D8] cursor-pointer"
                  aria-required="true"
                />
                <span className="text-xs text-slate-600 leading-relaxed group-hover:text-slate-800 transition-colors">
                  Acepto el{" "}
                  <Link
                    to="/aviso-de-privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="repair-privacy-link"
                    className="text-[#00B4D8] font-semibold hover:underline"
                  >
                    Aviso de Privacidad
                  </Link>{" "}
                  y autorizo el tratamiento de mis datos para responder mi
                  solicitud de {isFab ? "fabricación" : "reparación"}. <span className="text-[#00B4D8]">*</span>
                </span>
              </label>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-slate-500">
                Un técnico te contactará por teléfono el mismo día.
              </p>
              <button
                type="submit"
                disabled={loading || !acceptedPrivacy}
                data-testid="repair-submit-button"
                className="gf-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="inline-flex items-center gap-2">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  <span>{loading ? "Enviando" : "Enviar Solicitud"}</span>
                </span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
